import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const SocialMediaPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent mb-6">
            Social Media Marketing
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Build your brand, engage your audience, and drive sales with strategic social media marketing campaigns.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Social Media Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Build Brand Awareness</h3>
              <p className="text-gray-600">Increase visibility and recognition across social platforms where your customers spend time.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-group-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Engage Your Community</h3>
              <p className="text-gray-600">Connect directly with customers and build lasting relationships through authentic engagement.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Drive Sales</h3>
              <p className="text-gray-600">Convert followers into customers with strategic content and targeted campaigns.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-pink-600 to-red-600 bg-clip-text text-transparent">
            Our Social Media Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-calendar-check-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Content Planning</h4>
              <p className="text-sm text-gray-600">Strategic content calendars tailored to your audience</p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-red-100 p-6 rounded-xl">
              <i className="ri-image-edit-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Creative Design</h4>
              <p className="text-sm text-gray-600">Eye-catching graphics and visual content creation</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-send-plane-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Daily Posting</h4>
              <p className="text-sm text-gray-600">Consistent posting across all your social platforms</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-chat-3-line text-3xl text-orange-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Community Management</h4>
              <p className="text-sm text-gray-600">Engage with followers and manage interactions</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-green-100 p-6 rounded-xl">
              <i className="ri-advertisement-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Paid Advertising</h4>
              <p className="text-sm text-gray-600">Targeted ad campaigns to reach your ideal customers</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-line-chart-line text-3xl text-green-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Analytics & Reporting</h4>
              <p className="text-sm text-gray-600">Track performance and optimize for better results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Follow Our Social Media Journey</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Social Presence?</h2>
          <p className="text-xl mb-8">Let's create engaging content that drives real results for your business!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-pink-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-pink-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMediaPage;